/**
 * Reports service — frontend wrappers for the surviving Reports endpoints.
 *
 * Builder + saved reports live in `reportBuilderService.ts`. RunDetail streams
 * the execution doc from Firestore directly (no REST roundtrip). This file
 * holds the small set still consumed by:
 *   - RunDetail.tsx       (fetchArtifactSignedUrl)
 *   - SloSettings.tsx     (fetchSloSettings, updateSloConfig, upsertSloOverride, deleteSloOverride)
 */

import { apiRequest } from './api';
import type { SloConfig, SloOverride } from '@simcheck/shared-types';

// ----------------------------------------------------------------------------
// Artifact downloads (signed URLs)
// ----------------------------------------------------------------------------

/** Response shape of `GET /reports/executions/:id/artifacts/:artifactId/signed-url` (routes/artifacts.ts). */
export interface ArtifactSignedUrlResponse {
  artifactId: string;
  signedUrl: string;
  expiresAt: string;
  sizeBytes: number;
  contentType: string;
  filename: string;
}

export async function fetchArtifactSignedUrl(
  organizationId: string,
  executionId: string,
  artifactId: string,
  expiresInSeconds = 900,
): Promise<ArtifactSignedUrlResponse> {
  return apiRequest<ArtifactSignedUrlResponse>(
    `/reports/executions/${encodeURIComponent(executionId)}/artifacts/${encodeURIComponent(artifactId)}/signed-url?expiresIn=${expiresInSeconds}`,
    { method: 'GET' },
    organizationId,
  );
}

// ----------------------------------------------------------------------------
// SLO settings
// ----------------------------------------------------------------------------

export interface SloSettingsResponse {
  config: SloConfig;
  overrides: SloOverride[];
}

export async function fetchSloSettings(organizationId: string): Promise<SloSettingsResponse> {
  return apiRequest<SloSettingsResponse>(
    '/reports/slo',
    { method: 'GET' },
    organizationId,
  );
}

export async function updateSloConfig(
  organizationId: string,
  changes: Partial<SloConfig>,
): Promise<{ ok: true; updated: Partial<SloConfig> }> {
  return apiRequest(
    '/reports/slo',
    { method: 'PUT', body: JSON.stringify(changes) },
    organizationId,
  );
}

export async function upsertSloOverride(
  organizationId: string,
  override: Pick<SloOverride, 'scope' | 'scopeId' | 'metric' | 'threshold' | 'note'>,
): Promise<{ ok: true; id: string }> {
  return apiRequest(
    '/reports/slo/overrides',
    { method: 'POST', body: JSON.stringify(override) },
    organizationId,
  );
}

export async function deleteSloOverride(
  organizationId: string,
  overrideId: string,
): Promise<{ ok: true }> {
  return apiRequest(
    `/reports/slo/overrides/${encodeURIComponent(overrideId)}`,
    { method: 'DELETE' },
    organizationId,
  );
}
